// src/cockpit4/store.ts
// ============================================
// Cockpit 4.0 Workspace 状态管理
//
// 职责：
//   1. 维护当前激活的 Workspace（9 个 Tab）
//   2. 维护 Hermes 抽屉的展开 / 收起状态
//   3. 提供 setWorkspace / toggleHermesDrawer 等 action
//
// 设计：使用 zustand 进行轻量状态管理，避免 props 钻取
// ============================================

import { create } from "zustand";
// v6.24 角色守卫：setWorkspace 按角色限制可切换的工作区（游客仅 AI 超市）
import { useUserStore } from "../stores/userStore";

// ============================================
// 类型定义
// ============================================

/** Workspace 标识 */
export type WorkspaceId =
  | "ceo"
  | "marketing"
  | "crm"
  | "knowledge"
  | "projects"
  | "finance"
  | "workflow"
  | "settings"
  | "media_studio"
  | "geodso"
  | "boss_view"
  | "media_workbench";

/** CRM 风险客户条目（CRM → CEO 风险区联动） */
export interface CrmRiskItem {
  /** 客户名称 */
  customerName: string;
  /** 风险类型：stale=30天未联系 / closing=快成交未跟进 */
  type: "stale" | "closing";
  /** 距上次联系天数 */
  daysSinceContact: number;
  /** 客户所处阶段（跟进状态） */
  stage: string;
  /** 行业 */
  industry: string;
}

// ============================================
// v6.x Phase 1：角色(细分) → 可见 Workspace 规则（页面级裁剪）
// 与 WorkspaceHeader4 的导航渲染共用同一口径，避免导航与切换守卫不一致。
//   - super_admin：全量；guest：全览（数据由遮罩保护）
//   - employee+content：总控台/营销/内容工厂/设置
//   - employee+sales：总控台/客户中心/设置
//   - employee 未设细分：仅隐藏财务（兼容存量员工）
//   - bossMode（超管老板视图）：仍全量，视图优先由老板视图承载
// ============================================
const ROLE_WORKSPACE_ALLOW: Record<string, Partial<Record<WorkspaceId, boolean>>> = {
  employee_content: {
    ceo: true,
    marketing: true,
    media_studio: true,
    media_workbench: true,
    settings: true,
  },
  employee_sales: {
    ceo: true,
    crm: true,
    settings: true,
  },
};

/** 判断某角色(细分)是否可访问指定工作区（页面级） */
export function isWorkspaceAllowed(
  role: string | undefined,
  sub_role: string | undefined,
  ws: WorkspaceId
): boolean {
  if (role === "super_admin" || !role || role === "guest") return true;
  if (role === "employee") {
    if (sub_role !== "content" && sub_role !== "sales") {
      return ws !== "finance"; // 兼容旧行为
    }
    const allowed = ROLE_WORKSPACE_ALLOW["employee_" + sub_role];
    return allowed ? !!allowed[ws] : true;
  }
  return true;
}

/** 生成当前角色可见的 WorkspaceId 列表（用于默认落地页等） */
export function getAllowedWorkspaceIds(
  role: string | undefined,
  sub_role: string | undefined
): WorkspaceId[] {
  const all: WorkspaceId[] = [
    "ceo", "marketing", "media_studio", "crm", "knowledge",
    "projects", "finance", "workflow", "geodso", "boss_view", "media_workbench", "settings",
  ];
  return all.filter((w) => isWorkspaceAllowed(role, sub_role, w));
}

/** Cockpit 4.0 全局 Store */
export interface Cockpit4State {
  /** 当前激活的 Workspace */
  currentWorkspace: WorkspaceId;
  /** Hermes 抽屉是否展开（默认收起） */
  hermesDrawerOpen: boolean;
  /** CRM 风险客户列表（联动 CEO 风险监控面板） */
  crmRisks: CrmRiskItem[];

  /** 切换到指定 Workspace */
  setWorkspace: (ws: WorkspaceId) => void;
  /** 切换 Hermes 抽屉展开 / 收起 */
  toggleHermesDrawer: () => void;
  /** 直接设置 Hermes 抽屉状态 */
  setHermesDrawer: (open: boolean) => void;
  /** 设置 CRM 风险客户列表 */
  setCrmRisks: (risks: CrmRiskItem[]) => void;
}

// ============================================
// 创建 Store
// ============================================

export const useCockpit4Store = create<Cockpit4State>((set) => ({
  currentWorkspace: "ceo",
  hermesDrawerOpen: false,
  crmRisks: [],

  // v6.x Phase 1：setWorkspace 按角色(细分) + Workspace 统一口径守卫（与 header 导航一致）
  setWorkspace: (ws) => {
    const user = useUserStore.getState().currentUser;
    if (!isWorkspaceAllowed(user?.role, user?.sub_role, ws)) return;
    set({ currentWorkspace: ws });
  },
  toggleHermesDrawer: () =>
    set((state) => ({ hermesDrawerOpen: !state.hermesDrawerOpen })),
  setHermesDrawer: (open) => set({ hermesDrawerOpen: open }),
  setCrmRisks: (risks) => set({ crmRisks: risks }),
}));

export default useCockpit4Store;
